import { useEffect } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { SignIn, SignUp } from "@clerk/react"
import { useAuthGate, useLoginGate, type LoginReason } from "@/lib/authGate"

const COPY: Record<LoginReason, string> = {
  second_search: "Sign in to ask more. Your first search was free.",
  landing_search: "Sign in to keep searching. Your free search is used.",
  simulator: "Sign in to use the order simulators.",
  login_required: "Sign in to continue.",
  upload: "Sign in to attach documents.",
}

/**
 * Clerk sign-in (or sign-up) in a dialog: the only place Nira signs anyone in. Closes itself once
 * the visitor is signed in, then continues the flow. A Google sign-in leaves the page; Clerk brings
 * it back through /auth/login#/sso-callback (AuthRoute), then to `after`, or this page.
 */
// Clerk supports "virtual" routing at runtime, but the SignIn prop type omits it.
const signInRouting = "virtual" as unknown as "hash"

export function LoginModal() {
  const { open, reason, after, mode, close } = useLoginGate()
  const { signedIn } = useAuthGate()
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const back = after ?? `${pathname}${search}`

  useEffect(() => {
    if (!open || !signedIn) return
    const target = after
    close()
    // A guest thread is read-only for an account, so a signed-in visitor continues from the target.
    if (target && target !== pathname) navigate(target, { replace: true })
  }, [open, signedIn, after, close, navigate, pathname])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open, close])

  if (!open || signedIn) return null

  return (
    <div className="modal-bg" onClick={close}>
      <div
        className="login-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Sign in to NIRA"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="login-modal__msg">{COPY[reason ?? "login_required"]}</p>
        {mode === "up" ? (
          <SignUp routing={signInRouting} fallbackRedirectUrl={back} signInFallbackRedirectUrl={back} />
        ) : (
          <SignIn routing={signInRouting} fallbackRedirectUrl={back} signUpFallbackRedirectUrl={back} />
        )}
        <p className="login-modal__msg">
          By signing in you agree to our <Link to="/privacy" onClick={close}>Privacy Policy</Link>.
        </p>
        <button type="button" className="btn btn--ghost btn--sm" onClick={close}>
          Not now
        </button>
      </div>
    </div>
  )
}
